import type { ReactNode } from "react";

export type FaqSearchResult<T> = {
  item: T;
  index: number;
};

function normaliseFaqSearchText(value: string) {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("en-GB")
    .trim();
}

function getSearchableText(value: ReactNode): string {
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) return value.map(getSearchableText).join(" ");
  if (value && typeof value === "object" && "props" in value) {
    return getSearchableText((value as { props?: { children?: ReactNode } }).props?.children ?? "");
  }
  return "";
}

/**
 * Returns original FAQ objects and indices so search changes presentation only.
 * The source order remains the single input for visible questions and FAQPage
 * schema elsewhere in the page.
 */
export function filterFaqItems<T>(
  items: readonly T[],
  query: string,
  getQuestion: (item: T) => string,
  getAnswer: (item: T) => ReactNode,
): FaqSearchResult<T>[] {
  const terms = normaliseFaqSearchText(query).split(/\s+/).filter(Boolean);

  return items.flatMap((item, index) => {
    if (terms.length === 0) return [{ item, index }];

    const searchableText = normaliseFaqSearchText(`${getQuestion(item)} ${getSearchableText(getAnswer(item))}`);
    return terms.every((term) => searchableText.includes(term)) ? [{ item, index }] : [];
  });
}
